declare global {
  interface Window {
    dataLayer: Record<string, any>[];
  }
}

export interface GA4Item {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
  item_category?: string;
  index?: number;
  item_variant?: string;
  discount?: number;
  coupon?: string;
}

interface GA4UserData {
  email?: string;
  phone_number?: string;
  address?: {
    first_name?: string;
    last_name?: string;
    street?: string;
    city?: string;
    region?: string;
    postal_code?: string;
    country?: string;
  };
}

const PENDING_EVENTS_KEY = "gtm_pending_ecommerce_events";

function isConsentGranted(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const consent = localStorage.getItem("_iub_cs-64318772");
    if (!consent) return false;
    const parsed = JSON.parse(consent);
    return parsed.consent === true || parsed.purposes?.length > 0;
  } catch {
    return false;
  }
}

function savePendingEvent(event: string, params: Record<string, any>) {
  try {
    const pending = JSON.parse(sessionStorage.getItem(PENDING_EVENTS_KEY) || "[]");
    pending.push({ event, ecommerce: params });
    sessionStorage.setItem(PENDING_EVENTS_KEY, JSON.stringify(pending));
  } catch {
    // sessionStorage not available
  }
}

export function flushPendingEvents() {
  if (typeof window === "undefined") return;
  try {
    const pending = JSON.parse(sessionStorage.getItem(PENDING_EVENTS_KEY) || "[]");
    if (pending.length === 0) return;
    sessionStorage.removeItem(PENDING_EVENTS_KEY);
    for (const entry of pending) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({ event: entry.event, ecommerce: entry.ecommerce });
    }
  } catch {
    // ignore
  }
}

function pushToDataLayer(event: string, params: Record<string, any>) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({ event, ecommerce: params });

  if (!isConsentGranted()) {
    savePendingEvent(event, params);
  }
}

// --- Mapper functions ---

export function mapShopProductToGA4Item(
  product: {
    id: number;
    name: string;
    price: string;
    categories?: Array<{ name: string }>;
  },
  index?: number,
): GA4Item {
  return {
    item_id: product.id.toString(),
    item_name: product.name,
    price: parseFloat(product.price) || 0,
    quantity: 1,
    item_category: product.categories?.[0]?.name,
    ...(index !== undefined && { index }),
  };
}

export function mapShopCartItemToGA4Item(item: {
  id: number;
  name: string;
  price: number;
  quantity: number;
}): GA4Item {
  return {
    item_id: item.id.toString(),
    item_name: item.name,
    price: item.price,
    quantity: item.quantity,
  };
}

export function mapShopOrderItemToGA4Item(item: {
  id: number;
  name: string;
  price: number;
  quantity: number;
  sku?: string;
}): GA4Item {
  return {
    item_id: item.id.toString(),
    item_name: item.name,
    price: item.price,
    quantity: item.quantity,
  };
}

export function mapBookingCartItemToGA4Item(item: {
  Service?: string;
  LineNr?: number;
  Role?: string;
  GrossAmount?: { Price: number; Currency?: string };
  GrossPrice?: { Price: number; Currency?: string };
  Quantity?: { Total: number };
}): GA4Item {
  return {
    item_id: item.Service || item.LineNr?.toString() || "",
    item_name: item.Service || "Servizio",
    price: item.GrossAmount?.Price || item.GrossPrice?.Price || 0,
    quantity: item.Quantity?.Total || 1,
  };
}

export function mapBookingOrderLineToGA4Item(line: {
  Service?: string;
  LineNr?: number;
  Role?: string;
  Included?: boolean;
  GrossAmount?: { Price: number };
  GrossPrice?: { Price: number };
  Quantity?: { Total: number };
}): GA4Item {
  return {
    item_id: line.Service || line.LineNr?.toString() || "",
    item_name: line.Service || "Servizio",
    price: line.GrossAmount?.Price || line.GrossPrice?.Price || 0,
    quantity: line.Quantity?.Total || 1,
  };
}

// --- Event functions ---

export function trackGA4ViewItemList(items: GA4Item[], listName?: string) {
  pushToDataLayer("view_item_list", {
    item_list_id: listName || "shop_products",
    item_list_name: listName || "Shop Products",
    items,
    currency: "EUR",
  });
}

export function trackGA4ViewItem(items: GA4Item[], value: number) {
  pushToDataLayer("view_item", {
    currency: "EUR",
    value,
    items,
  });
}

export function trackGA4AddToCart(items: GA4Item[], value: number, currency = "EUR") {
  pushToDataLayer("add_to_cart", {
    currency,
    value,
    items,
  });
}

export function trackGA4RemoveFromCart(items: GA4Item[], value: number, currency = "EUR") {
  pushToDataLayer("remove_from_cart", {
    currency,
    value,
    items,
  });
}

export function trackGA4ViewCart(items: GA4Item[], value: number, currency = "EUR") {
  pushToDataLayer("view_cart", {
    currency,
    value,
    items,
  });
}

export function trackGA4BeginCheckout(
  items: GA4Item[],
  value: number,
  currency = "EUR",
  coupon?: string,
) {
  pushToDataLayer("begin_checkout", {
    currency,
    value,
    items,
    ...(coupon && { coupon }),
  });
}

export function trackGA4AddPaymentInfo(
  items: GA4Item[],
  value: number,
  currency = "EUR",
  paymentType?: string,
) {
  pushToDataLayer("add_payment_info", {
    currency,
    value,
    items,
    ...(paymentType && { payment_type: paymentType }),
  });
}

export function trackGA4Purchase(
  transactionId: string,
  items: GA4Item[],
  value: number,
  currency = "EUR",
  userData?: GA4UserData,
  coupon?: string,
) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });

  const params = {
    transaction_id: transactionId,
    currency,
    value,
    items,
    ...(coupon && { coupon }),
    ...(userData && { user_data: userData }),
  };

  window.dataLayer.push({
    event: "purchase",
    ...params,
    ecommerce: params,
  });

  if (!isConsentGranted()) {
    savePendingEvent("purchase", params);
  }
}
